import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./Navbar.css";
import "./Navbar.auth.css";

const Navbar = () => {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();

  const exploreRef = useRef(null);
  const userMenuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isLoggedIn = !!user;

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const closeMenu = () => {
    setMenuOpen(false);
    setExploreOpen(false);
    setUserMenuOpen(false);
  };

  const toggleExplore = () => setExploreOpen((prev) => !prev);
  const toggleUserMenu = () => setUserMenuOpen((prev) => !prev);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (exploreRef.current && !exploreRef.current.contains(event.target)) {
        setExploreOpen(false);
      }

      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleSignOut = async () => {
    closeMenu();

    try {
      await signOut();
    } catch (err) {
      console.error("Sign-out failed:", err);
    }

    navigate("/", { replace: true });
  };

  const avatarInitial = (user?.display_name || user?.full_name || user?.email || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const displayName = user?.display_name || user?.full_name || user?.email || "Account";
  const brandTarget = isLoggedIn ? "/dashboard" : "/";

  return (
    <header className="navbar" role="banner">
      <nav className="navbar__inner" aria-label="Main navigation">
        <NavLink
          to={brandTarget}
          className="navbar__brand"
          aria-label="QuitPact home"
          onClick={closeMenu}
        >
          <img
            src="/logo.png"
            alt="QuitPact logo"
            className="navbar__logo-img"
          />
        </NavLink>

        <button
          className={`navbar__hamburger ${menuOpen ? "navbar__hamburger--open" : ""}`}
          onClick={toggleMenu}
          aria-expanded={menuOpen}
          aria-controls="navbar-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>

        <ul
          id="navbar-menu"
          className={`navbar__links ${menuOpen ? "navbar__links--open" : ""}`}
        >
          {(loading || !isLoggedIn) && (
            <>
              <li>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                  end
                >
                  Home
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/check-in"
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Check-In
                </NavLink>
              </li>

              <li className="navbar__dropdown" ref={exploreRef}>
                <button
                  className="navbar__link navbar__dropdown-toggle"
                  onClick={toggleExplore}
                  aria-expanded={exploreOpen}
                  type="button"
                >
                  Explore
                  {exploreOpen ? (
                    <ChevronUp className="navbar__chevron-icon" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="navbar__chevron-icon" aria-hidden="true" />
                  )}
                </button>

                {exploreOpen && (
                  <ul className="navbar__dropdown-menu">
                    <li>
                      <NavLink
                        to="/cost-calculator"
                        className="navbar__dropdown-item"
                        onClick={closeMenu}
                      >
                        Cost Calculator
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/health-awareness"
                        className="navbar__dropdown-item"
                        onClick={closeMenu}
                      >
                        Health Awareness
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/quit-benefits"
                        className="navbar__dropdown-item"
                        onClick={closeMenu}
                      >
                        Quit Benefits
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/smoking-story"
                        className="navbar__dropdown-item"
                        onClick={closeMenu}
                      >
                        Smokes & Stats
                      </NavLink>
                    </li>
                  </ul>
                )}
              </li>

              <li className="navbar__auth">
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `navbar__link navbar__link--cta ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Sign In
                </NavLink>
              </li>
            </>
          )}

          {!loading && isLoggedIn && (
            <>
              <li>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                  end
                >
                  Progress Tracking
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/check-in"
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Check In
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/quit-plan"
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Quit Plan
                </NavLink>
              </li>

              <li className="navbar__auth navbar__dropdown" ref={userMenuRef}>
                <button
                  className="navbar__user-toggle"
                  onClick={toggleUserMenu}
                  aria-expanded={userMenuOpen}
                  aria-label={`Account menu for ${displayName}`}
                  type="button"
                >
                  {user.picture_url ? (
                    <img
                      src={user.picture_url}
                      alt=""
                      className="navbar__avatar"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span
                      className="navbar__avatar navbar__avatar--fallback"
                      aria-hidden="true"
                    >
                      {avatarInitial}
                    </span>
                  )}

                  <span className="navbar__user-name">{displayName}</span>

                  {userMenuOpen ? (
                    <ChevronUp className="navbar__user-chevron" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="navbar__user-chevron" aria-hidden="true" />
                  )}
                </button>

                {userMenuOpen && (
                  <ul className="navbar__dropdown-menu navbar__dropdown-menu--right">
                    <li className="navbar__user-email">{user.email}</li>
                    <li>
                      <NavLink
                        to="/profile"
                        className="navbar__dropdown-item"
                        onClick={closeMenu}
                      >
                        Profile
                      </NavLink>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="navbar__dropdown-item navbar__dropdown-item--button"
                        onClick={handleSignOut}
                      >
                        Sign out
                      </button>
                    </li>
                  </ul>
                )}
              </li>
            </>
          )}
        </ul>
      </nav>
    </header>
  );
};

export default Navbar;
